import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash/debounce'

export default class extends Controller {
  static targets = ['form', 'refreshInput', 'submitter']
  static values = {
    autoSubmit: { type: Boolean, default: false },
    autoSubmitDelay: { type: Number, default: 100 },
    debounceDelay: { type: Number, default: 0 }
  }

  connect () {
    // Auto-submit the form when controller connects if the form has the auto-submit action
    if (this.autoSubmitValue) {
      setTimeout(() => {
        this.submit()
      }, this.autoSubmitDelayValue)
    }

    this.pendingSubmit = false
    this.debouncedRequestSubmit = debounce((form, submitter) => {
      this.pendingSubmit = false
      form.requestSubmit(submitter)
    }, this.debounceDelayValue)

    this.editingSnapshot = null
    this.submissionsInFlight = 0

    this.preserveActiveFieldOnMorph = this.preserveActiveFieldOnMorph.bind(this)
    this.snapshotEditingField = this.snapshotEditingField.bind(this)
    this.forgetEditingField = this.forgetEditingField.bind(this)
    this.markMorphing = this.markMorphing.bind(this)
    this.restoreEditingField = this.restoreEditingField.bind(this)

    document.addEventListener('turbo:before-morph-element', this.preserveActiveFieldOnMorph)
    this.element.addEventListener('input', this.snapshotEditingField, true)
    this.element.addEventListener('focusout', this.forgetEditingField, true)
    this.element.addEventListener('turbo:submit-start', this.markMorphing)
    this.element.addEventListener('turbo:submit-end', this.restoreEditingField)
  }

  disconnect () {
    document.removeEventListener('turbo:before-morph-element', this.preserveActiveFieldOnMorph)
    this.element.removeEventListener('input', this.snapshotEditingField, true)
    this.element.removeEventListener('focusout', this.forgetEditingField, true)
    this.element.removeEventListener('turbo:submit-start', this.markMorphing)
    this.element.removeEventListener('turbo:submit-end', this.restoreEditingField)
  }

  submit (e) {
    if (this.#shouldSkipSubmit(e)) return

    const form = this.#resolveForm(e)
    if (!form) return

    const submitter = this.hasSubmitterTarget ? this.submitterTarget : undefined

    if (this.debounceDelayValue > 0 && !e?.detail?.bypassDebounce) {
      this.pendingSubmit = true
      this.debouncedRequestSubmit(form, submitter)
    } else {
      form.requestSubmit(submitter)
    }
  }

  // Mutating links inside the form (add/delete a line) trigger a Turbo request
  // that morphs the section. When the user edits a field and clicks one of these
  // links before blurring, the edit is only queued as a debounced submit and the
  // morph would wipe it (and the server would persist the new line against the
  // stale, un-saved value). We flush the queued submit first and defer the link
  // until that submit lands, so edits are persisted before the link's request runs.
  saveThenFollow (event) {
    const link = event.currentTarget

    if (!this.pendingSubmit || !this.hasFormTarget) return

    event.preventDefault()
    event.stopImmediatePropagation()

    // The flushed submit morphs the row that contains this link, which can detach
    // `link`. A click on a detached node cannot bubble up to Turbo, so once the
    // save lands we re-resolve the equivalent link in the live DOM (it carries the
    // same turbo-method/turbo-stream data attributes) and click that instead. The
    // re-click re-enters this handler, but `pendingSubmit` is already false by then
    // so it falls through to Turbo's normal link handling.
    const href = link.href
    const form = this.formTarget
    const onSubmitEnd = (e) => {
      if (e.target !== form) return
      form.removeEventListener('turbo:submit-end', onSubmitEnd)

      const target = link.isConnected ? link : this.#findLinkByHref(href)
      if (target) target.click()
    }

    form.addEventListener('turbo:submit-end', onSubmitEnd)
    this.debouncedRequestSubmit.flush()
  }

  markBusy () {
    const form = this.hasFormTarget ? this.formTarget : null
    if (form) form.setAttribute('aria-busy', 'true')
  }

  // For cases where we want to refresh the form to apply display changes
  // but not submit the form, we use a hidden input to indicate to the controller
  // that the view should be re-rendered and the form object should not be submitted
  refresh (e) {
    if (!this.hasRefreshInputTarget) return
    if (this.#shouldSkipSubmit(e)) return

    // Close native select picker on mobile before Turbo replaces the DOM,
    // otherwise the browser restores focus on the new element and reopens the picker
    if (e?.target?.tagName === 'SELECT') {
      e.target.blur()
    }

    const form = this.#resolveForm(e)
    if (!form) return

    this.refreshInputTarget.removeAttribute('disabled')
    form.setAttribute('novalidate', 'novalidate')
    this.submit(e)
  }

  // Turbo Stream morphs (turbo_stream.replace/update with method: :morph) do not set
  // idiomorph's `ignoreActiveValue`, unlike full-page morphs. So when an auto-submit
  // response morphs the form while the user is typing in a field, idiomorph overwrites
  // that focused input with the server-rendered value, wiping the keystrokes that were
  // not part of the submitted payload. Cancelling the morph for the focused field keeps
  // its value and caret intact when the node is morphed in place. Checkables whose
  // debounced submit has not fired yet are preserved the same way, or the morph would
  // reset them to the pre-click server state and the submit would post stale values.
  preserveActiveFieldOnMorph (e) {
    const element = e.target
    if (!this.element.contains(element)) return

    if (this.#isCheckable(element) && this.pendingSubmit) {
      e.preventDefault()
      return
    }

    if (element !== document.activeElement) return
    if (!this.#isEditableTextField(element)) return

    e.preventDefault()
  }

  // Remember the field the user is editing, so it can be restored if a morph replaces
  // its surrounding markup instead of morphing it in place (which detaches the input and
  // drops the focus, losing keystrokes that the server has not yet echoed back).
  snapshotEditingField (e) {
    const field = e.target
    if (!this.#isEditableTextField(field) || !field.name) return

    let start = null
    let end = null
    try {
      start = field.selectionStart
      end = field.selectionEnd
    } catch {
      // Inputs like number/date/email do not expose a text selection.
    }

    this.editingSnapshot = { name: field.name, value: field.value, start, end }
  }

  // Only forget the edited field when the blur comes from the user (Tab, click elsewhere),
  // not from a morph detaching the node. Refreshes can overlap (a change and a blur both
  // submit), so the blur of the last render must not count as the user leaving the field
  // while any submission is still in flight.
  forgetEditingField (e) {
    if (this.submissionsInFlight > 0) return
    if (this.editingSnapshot && e.target.name === this.editingSnapshot.name) {
      this.editingSnapshot = null
    }
  }

  markMorphing (e) {
    if (e.target !== this.element) return
    this.submissionsInFlight += 1
  }

  restoreEditingField (e) {
    if (e.target !== this.element) return

    window.requestAnimationFrame(() => {
      this.submissionsInFlight = Math.max(0, this.submissionsInFlight - 1)

      const snapshot = this.editingSnapshot
      if (!snapshot) return

      // If focus is still on an editable field of this form, the morph preserved it (or the
      // user intentionally moved on): never steal focus back in that case.
      const active = document.activeElement
      if (active && this.element.contains(active) && this.#isEditableTextField(active)) return

      const field = this.element.querySelector(`[name="${window.CSS.escape(snapshot.name)}"]`)
      if (!field) return

      if (field.value !== snapshot.value) field.value = snapshot.value
      field.focus()
      if (snapshot.start !== null) {
        try {
          field.setSelectionRange(snapshot.start, snapshot.end)
        } catch {
          // Selection not supported for this input type.
        }
      }
    })
  }

  #resolveForm (e) {
    if (this.hasFormTarget) return this.formTarget
    if (e?.target?.form) return e.target.form

    return null
  }

  #findLinkByHref (href) {
    const links = this.element.querySelectorAll('a[data-turbo-method]')
    return Array.from(links).find((a) => a.href === href)
  }

  #shouldSkipSubmit (e) {
    if (!e || !e.target || typeof e.target.getAttribute !== 'function') return false
    return (e && e.target.getAttribute('data-form-submit-ignore-event') === e.type)
  }

  #isEditableTextField (element) {
    if (element.tagName === 'TEXTAREA') return true
    if (element.tagName !== 'INPUT') return false

    const preservedTypes = ['text', 'number', 'email', 'tel', 'url', 'search', 'date', 'password']
    return preservedTypes.includes(element.type)
  }

  #isCheckable (element) {
    return element.tagName === 'INPUT' && (element.type === 'checkbox' || element.type === 'radio')
  }
}
